import { useCallback, useState } from "react";
import { LOCALSTORAGENAME } from "../data/constants";

/**
 * Epic 82838: the Post Interview list remembers its status + department filters per user, so HR
 * comes back to the list they were working. Stored under `postif-hr-filters:{userId}` (a shared
 * machine must not inherit another user's filters) and read synchronously in the useState
 * initialiser, so the first render - and the first fetch's filtering - already has them.
 *
 * Same safe-read/safe-write shape as usePersistedColumnVisibility: a corrupt value or disabled
 * storage falls back to the defaults and never breaks the page.
 */

export interface PifListFilters {
  status: string;
  department: string;
  /** The HR list's HR Coordinator filter: "all", "unassigned" or a Portal UserId. */
  coordinator?: string;
}

const ANONYMOUS_USER = "anonymous";

const storageKeyFor = (userId: number | null | undefined) =>
  `${LOCALSTORAGENAME.POSTIFHRFILTERS}:${userId || ANONYMOUS_USER}`;

const readFilters = (key: string, defaults: PifListFilters, isValidStatus: (status: string) => boolean): PifListFilters => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return defaults;
    return {
      // A status the list no longer offers would leave the select blank and the grid empty.
      status: typeof parsed.status === "string" && isValidStatus(parsed.status) ? parsed.status : defaults.status,
      department: typeof parsed.department === "string" && parsed.department ? parsed.department : defaults.department,
      coordinator: typeof parsed.coordinator === "string" && parsed.coordinator ? parsed.coordinator : defaults.coordinator,
    };
  } catch {
    return defaults;
  }
};

export const usePersistedPifFilters = (
  userId: number | null | undefined,
  defaults: PifListFilters,
  isValidStatus: (status: string) => boolean,
) => {
  const storageKey = storageKeyFor(userId);

  const [filters, setFilters] = useState(() => ({
    key: storageKey,
    value: readFilters(storageKey, defaults, isValidStatus),
  }));

  // A login or impersonation switch can change the user without remounting the page: load that
  // user's own filters rather than carrying the previous user's over.
  if (filters.key !== storageKey) {
    setFilters({ key: storageKey, value: readFilters(storageKey, defaults, isValidStatus) });
  }

  const updateFilters = useCallback(
    (change: Partial<PifListFilters>) => {
      setFilters((current) => {
        const value = { ...current.value, ...change };
        try {
          localStorage.setItem(storageKey, JSON.stringify(value));
        } catch {
          // Storage disabled or full - the filter still applies for this visit.
        }
        return { key: storageKey, value };
      });
    },
    [storageKey],
  );

  return { filters: filters.value, updateFilters };
};
